import { Button, message } from "antd";
import { useEffect, useRef, useState } from "react";
import CustomModal from "../../../../components/CustomModal";

interface CameraCaptureModalProps {
  open: boolean;
  onClose: () => void;
  onCapture: (file: File) => void;
}

export default function CameraCaptureModal({
  open,
  onClose,
  onCapture,
}: CameraCaptureModalProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const context = canvas.getContext("2d");

      if (context) {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        context.drawImage(video, 0, 0, canvas.width, canvas.height);

        const imageUrl = canvas.toDataURL("image/png");
        setCapturedImage(imageUrl);
      }
    }
  };

  const retakePhoto = () => {
    setCapturedImage(null);
  };

  const confirmCapture = () => {
    if (capturedImage) {
      // Convert base64 to File object
      fetch(capturedImage)
        .then((res) => res.blob())
        .then((blob) => {
          const file = new File([blob], "captured-photo.png", {
            type: "image/png",
          });
          onCapture(file);
          handleClose();
        });
    }
  };

  const handleClose = () => {
    setCapturedImage(null);
    onClose();
  };

  useEffect(() => {
    if (stream && videoRef.current && !capturedImage) {
      videoRef.current.srcObject = stream;
    }
  }, [capturedImage, stream]);

  useEffect(() => {
    let currentStream: MediaStream | null = null;

    const initCamera = async () => {
      try {
        const mediaStream = await navigator.mediaDevices.getUserMedia({
          video: true,
        });
        currentStream = mediaStream;
        setStream(mediaStream);
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
        }
      } catch (error) {
        console.error("Error accessing camera:", error);
        message.error(
          "ক্যামেরায় প্রবেশ করা যাচ্ছে না। অনুগ্রহ করে ক্যামেরার অনুমতি দিন।"
        );
      }
    };

    if (open) {
      initCamera();
    }

    return () => {
      if (currentStream) {
        currentStream.getTracks().forEach((track) => track.stop());
      }
      setStream(null);
    };
  }, [open]);

  return (
    <CustomModal
      handleClose={handleClose}
      title="ফটো তুলুন"
      open={open}
      onCancel={handleClose}
      footer={[
        <Button key="cancel" onClick={handleClose}>
          বাতিল করুন
        </Button>,
        capturedImage ? (
          <>
            <Button
              key="retake"
              className="border-theme-primary text-theme-primary"
              onClick={retakePhoto}
            >
              পুনরায় তুলুন
            </Button>
            <Button key="confirm" type="primary" onClick={confirmCapture}>
              নিশ্চিত করুন
            </Button>
          </>
        ) : (
          <Button key="capture" type="primary" onClick={capturePhoto}>
            ফটো তুলুন
          </Button>
        ),
      ]}
      width={600}
      centered
    >
      <div className="flex flex-col items-center justify-center gap-4">
        {!capturedImage ? (
          <div className="relative w-full h-full bg-black rounded-lg overflow-hidden aspect-video">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              className="w-full h-full object-cover"
            />
          </div>
        ) : (
          <div className="w-full h-full bg-black rounded-lg overflow-hidden aspect-video">
            <img
              src={capturedImage}
              alt="Captured"
              className="w-full h-full object-cover"
            />
          </div>
        )}
        <canvas ref={canvasRef} className="hidden" />
      </div>
    </CustomModal>
  );
}
